import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import Card from './Card';
import Button from './Button';
import TextField from './TextField';
import Select from './Select';
import CustomAutocomplete from './CustomAutocomplete';
import ContentTypeEditor from './ContentTypeEditor';
import ContentBlocksEditor from './ContentBlocksEditor';
import TestEditor from './TestEditor';
import TestsArrayJsonImport from './TestsArrayJsonImport';
import MemoryGame from '../MemoryGame';
import TagCloud from '../TagCloud';
import Labyrinth from './Labyrinth';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Quiz as QuizIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ViewModule as ViewModuleIcon,
  AttachFile as AttachFileIcon,
  Code as CodeIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectAllProfessions, selectProfessionsLoading } from '../../redux/slices/professionsSlice';
import { selectAllSkills, selectSkillsLoading } from '../../redux/slices/skillsSlice';
import { toast } from 'react-toastify';
import { testsAPI } from '../../services/api';

// Универсальный редактор урока для создания/редактирования
// Используется в CourseCreate, CourseEdit, EditLessonPage

const LessonEditor = React.memo(({
  lesson,
  onChange,
  contentTabLabels = {},
  header, // Новый проп для заголовка урока
  isAdminOrTeacher = false, // Флаг для определения прав пользователя
  showTestsTab = true, // Флаг для отображения вкладки с тестами
  onProfessionsSync, // Callback для синхронизации профессий с курсом
  uniqueId, // Уникальный идентификатор для урока (например, moduleIndex-lessonIndex)
}) => {
  // Состояние для активного таба
  const [activeTab, setActiveTab] = useState('content');

  // Состояние для модального окна подтверждения удаления теста
  const [deleteTestDialog, setDeleteTestDialog] = useState({
    open: false,
    testId: null,
    testTitle: ''
  });

  // Состояние для превью контента
  const [contentPreviewOpen, setContentPreviewOpen] = useState(false);
  
  // Используем хук useTranslation для доступа к переводам
  const { t } = useTranslation(['lessons']);
  
  // Мемоізовані Redux селектори
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const skills = useSelector(selectAllSkills);
  const skillsLoading = useSelector(selectSkillsLoading);

  // Мемоізовані обробники
  const handleFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    const processedValue = name === 'duration' ? Number(value) : value;
    onChange({ ...lesson, [name]: processedValue });
  }, [lesson, onChange]);

  const handleProfessionsChange = useCallback((newValue) => {
    const professionIds = newValue.map(prof =>
      typeof prof === 'object' ? prof.id || prof._id : prof
    );
  
    onChange({ ...lesson, professions: professionIds });
    
    // Уведомляем родительский компонент о изменении профессий для синхронизации с курсом
    if (onProfessionsSync) {
      onProfessionsSync(professionIds);
    }
  }, [lesson, onChange, onProfessionsSync]);

  const handleProfessionChipDelete = useCallback((index) => {
    const currentProfessions = professions.filter(prof => {
      const profId = String(prof.id || prof._id);
      return (lesson.professions || []).some(lessonProf => {
        const lessonProfId = typeof lessonProf === 'object' 
          ? String(lessonProf.id || lessonProf._id) 
          : String(lessonProf);
        return profId === lessonProfId;
      });
    });
    
    const newProfessions = currentProfessions.filter((_, i) => i !== index);
    const professionIds = newProfessions.map(prof =>
      typeof prof === 'object' ? prof.id || prof._id : prof
    );
    
    onChange({ ...lesson, professions: professionIds });
    
    if (onProfessionsSync) {
      onProfessionsSync(professionIds);
    }
  }, [lesson, onChange, onProfessionsSync, professions]);

  const handleSkillsChange = useCallback((newValue) => {
    const skillsData = newValue.map(skill => {
      if (typeof skill === 'object') {
        return {
          id: skill.id || skill._id,
          name: skill.name || skill.title || '',
          responsibility_id: skill.responsibility_id || '',
          responsibility_name: skill.responsibility_name || '',
          tool_id: skill.tool_id || '',
          tool_name: skill.tool_name || '',
          description: skill.description || ''
        };
      }
      return skill;
    });
    onChange({ ...lesson, skills: skillsData });
  }, [lesson, onChange]);

  const handleSkillChipDelete = useCallback((index) => {
    const currentSkills = skills.filter(skill => {
      const skillId = String(skill.id || skill._id);
      return (lesson.skills || []).some(lessonSkill => {
        const lessonSkillId = typeof lessonSkill === 'object'
          ? String(lessonSkill.id || lessonSkill._id)
          : String(lessonSkill);
        return skillId === lessonSkillId;
      });
    });
    
    const newSkills = currentSkills.filter((_, i) => i !== index);
    const skillsData = newSkills.map(skill => {
      if (typeof skill === 'object') {
        return {
          id: skill.id || skill._id,
          name: skill.name || skill.title || '',
          responsibility_id: skill.responsibility_id || '',
          responsibility_name: skill.responsibility_name || '',
          tool_id: skill.tool_id || '',
          tool_name: skill.tool_name || '',
          description: skill.description || ''
        };
      }
      return skill;
    });
    
    onChange({ ...lesson, skills: skillsData });
  }, [lesson, onChange, skills]);

  // Обработчики для фото
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      console.log('No file selected');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      console.log('Invalid file type:', file.type);
      alert(t('lessonEditor.imageTypeError'));
      e.target.value = '';
      return;
    }

    // Проверяем размер файла (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('lessonEditor.imageSizeError'));
      e.target.value = '';
      return;
    }
    
    // Просто обновляем локальное состояние с файлом
    // Запрос на сервер будет отправлен при сохранении урока
    const updatedLesson = { 
      ...lesson, 
      imageFile: file, // Сохраняем файл для последующей отправки
      imagePreview: URL.createObjectURL(file) // Создаем превью для отображения
    };
    
    onChange(updatedLesson);
    
    // Очищаем input после выбора файла
    e.target.value = '';
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    console.log('handleImageUrlChange called with URL:', url);
    const updatedLesson = { 
      ...lesson, 
      image: url
    };
    
    onChange(updatedLesson);
  };

  const handleRemoveImage = () => {
    // Очищаем URL превью если он был создан
    if (lesson.imagePreview) {
      URL.revokeObjectURL(lesson.imagePreview);
    }
    
    const updatedLesson = { 
      ...lesson, 
      image: '', 
      imageFile: null,
      imagePreview: null
    };
    
    onChange(updatedLesson);
  };

  // Контент
  const handleContentChange = (content) => {
    onChange({ ...lesson, content });
  };
  const handleContentTypeChange = (contentType) => {
    onChange({ ...lesson, contentType });
  };

  // Контент-блоки
  const handleContentBlocksChange = (blocks) => {
    onChange({ ...lesson, contentBlocks: blocks, contentType: 'mixed' });
  };
  const addContentBlock = () => {
    const blocks = lesson.contentBlocks || [];
    onChange({
      ...lesson,
      contentBlocks: [
        ...blocks,
        { type: 'standard', content: '', order: blocks.length },
      ],
      contentType: 'mixed',
    });
  };
  const removeContentBlock = (blockIndex) => {
    const blocks = [...(lesson.contentBlocks || [])];
    blocks.splice(blockIndex, 1);
    onChange({ ...lesson, contentBlocks: blocks });
  };
  const switchToSingleContent = () => {
    // Берём первый блок как основной контент
    if (lesson.contentBlocks && lesson.contentBlocks.length > 0) {
      const first = lesson.contentBlocks[0];
      onChange({
        ...lesson,
        content: first.content,
        contentType: first.type,
        contentBlocks: [],
      });
    }
  };
  const switchToContentBlocks = () => {
    if (!lesson.contentBlocks || lesson.contentBlocks.length === 0) {
      onChange({
        ...lesson,
        contentBlocks: [
          {
            type: lesson.contentType || 'standard',
            content: lesson.content || '',
            order: 0,
          },
        ],
        contentType: 'mixed',
      });
    }
  };

  // Материалы (resources)
  const addResource = () => {
    const resources = lesson.resources || [];
    onChange({
      ...lesson,
      resources: [
        ...resources,
        {
          title: '',
          description: '',
          url: '',
          type: 'link',
          duration: '',
          fileType: '',
          codeLanguage: '',
          slidesCount: '',
        },
      ],
    });
  };
  const removeResource = (idx) => {
    const resources = [...(lesson.resources || [])];
    resources.splice(idx, 1);
    onChange({ ...lesson, resources });
  };
  const handleResourceChange = (idx, e) => {
    const { name, value } = e.target;
    const resources = [...(lesson.resources || [])];
    resources[idx] = { ...resources[idx], [name]: value };
    onChange({ ...lesson, resources });
  };

  // Практические задания
  const addExercise = () => {
    const exercises = lesson.practiceExercises || [];
    onChange({
      ...lesson,
      practiceExercises: [
        ...exercises,
        { title: '', description: '', codeSnippet: '', playgroundUrl: '' },
      ],
    });
  };
  const removeExercise = (idx) => {
    const exercises = [...(lesson.practiceExercises || [])];
    exercises.splice(idx, 1);
    onChange({ ...lesson, practiceExercises: exercises });
  };
  const handleExerciseChange = (idx, e) => {
    const { name, value } = e.target;
    const exercises = [...(lesson.practiceExercises || [])];
    exercises[idx] = { ...exercises[idx], [name]: value };
    onChange({ ...lesson, practiceExercises: exercises });
  };

  // Тесты урока
  const addTest = () => {
    const tempId = `temp-test-${Date.now()}-${Math.random()}`;
    const tests = lesson.tests || [];
    onChange({
      ...lesson,
      tests: [
        ...tests,
        {
          _id: tempId,
          title: '',
          description: '',
          timeLimit: 15,
          passingScore: 70,
          questions: [
            {
              type: 'single-choice',
              question: '',
              options: [
                { text: '', isCorrect: true },
                { text: '', isCorrect: false },
                { text: '', isCorrect: false },
                { text: '', isCorrect: false },
              ],
              points: 1,
            },
          ],
        },
      ],
    });
  };

  // Открыть модальное окно подтверждения удаления теста
  const handleOpenDeleteDialog = (testId) => {
    const tests = [...(lesson.tests || [])];
    const testToRemove = tests.find(test => test._id === testId);

    if (!testToRemove) {
      console.error('Тест не найден');
      return;
    }

    setDeleteTestDialog({
      open: true,
      testId: testId,
      testTitle: testToRemove.title || t('lessonEditor.unnamedTest')
    });
  };

  // Закрыть модальное окно подтверждения удаления теста
  const handleCloseDeleteDialog = () => {
    setDeleteTestDialog({
      open: false,
      testId: null,
      testTitle: ''
    });
  };

  // Подтвердить удаление теста
  const handleConfirmDeleteTest = async () => {
    const testId = deleteTestDialog.testId;

    if (!testId) {
      return;
    }

    // Если у теста есть ID и он не временный (не начинается на "temp"), удаляем через API
    if (testId && !testId.toString().startsWith('temp')) {
      try {
        await testsAPI.delete(testId);
        toast.success(t('lessonEditor.testDeletedSuccess'));
      } catch (error) {
        console.error('Ошибка при удалении теста:', error);
        toast.error(t('lessonEditor.testDeletedError'));
        handleCloseDeleteDialog();
        return; // Не удаляем из локального состояния, если не удалось удалить на сервере
      }
    }

    // Удаляем тест из локального состояния
    const tests = [...(lesson.tests || [])];
    const updatedTests = tests.filter(test => test._id !== testId);
    onChange({ ...lesson, tests: updatedTests });

    // Закрываем модальное окно
    handleCloseDeleteDialog();
  };
  const handleTestChange = (updatedTest, testIndex) => {
    const tests = [...(lesson.tests || [])];
    tests[testIndex] = updatedTest;
    onChange({ ...lesson, tests });
  };
  const handleTestsArrayImport = (testsArray) => {
    const tests = lesson.tests || [];
    onChange({
      ...lesson,
      tests: [...tests, ...testsArray],
    });
    toast.success(t('courses:courseCreate.ui.test.importArraySuccess', { moduleTitle: lesson.title }));
  };

  // Функция для рендеринга превью контента для одного блока
  const renderBlockPreview = (blockContent, blockType) => {
    try {
      switch (blockType) {
        case 'memoryGame':
          const memoryCards = JSON.parse(blockContent);
          return (
            <Box sx={{ minHeight: '300px' }}>
              <MemoryGame options={memoryCards} onChange={() => {}} />
            </Box>
          );
        case 'tagCloud':
          const tagCloudItems = JSON.parse(blockContent);
          return (
            <Box sx={{ minHeight: '300px' }}>
              <TagCloud items={tagCloudItems} />
            </Box>
          );
        case 'labyrinth':
          const labyrinthData = JSON.parse(blockContent);
          return (
            <Box sx={{ minHeight: '400px' }}>
              <Labyrinth data={labyrinthData} />
            </Box>
          );
        case 'flippingCards':
          const flippingCardsData = JSON.parse(blockContent);
          return (
            <Box sx={{ minHeight: '300px' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('lessons:editor.flippingCardsPreviewNote')} ({flippingCardsData.length} {t('lessons:editor.cards.count')})
              </Typography>
              <Grid container spacing={2}>
                {flippingCardsData.slice(0, 3).map((card, idx) => (
                  <Grid item xs={12} sm={6} md={4} key={idx}>
                    <Card sx={{ p: 2, minHeight: '150px' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {card?.front?.title || ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card?.front?.content?.substring(0, 100) || ''}...
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {flippingCardsData.length > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('lessons:editor.andMore', { count: flippingCardsData.length - 3 })}
                </Typography>
              )}
            </Box>
          );
        case 'standard':
          return (
            <Box
              sx={{
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  mb: 2,
                },
                '& th, & td': {
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: 'background.default',
                  fontWeight: 'bold',
                },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {blockContent || ''}
              </ReactMarkdown>
            </Box>
          );
        case 'presentation':
        case 'htmlBlock':
        case 'fullHtml':
          return (
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
              dangerouslySetInnerHTML={{ __html: blockContent }}
            />
          );
        default:
          return (
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
              dangerouslySetInnerHTML={{ __html: blockContent }}
            />
          );
      }
    } catch (error) {
      console.error('Error rendering block preview:', error);
      return (
        <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="body2" color="error.contrastText">
            {t('lessons:editor.previewError')}: {error.message}
          </Typography>
        </Box>
      );
    }
  };

  // Функция для рендеринга превью контента
  const renderContentPreview = () => {
    if (!contentPreviewOpen) return null;

    // Если режим mixed или есть блоки контента
    if (lesson.contentType === 'mixed' || (lesson.contentBlocks && lesson.contentBlocks.length > 0)) {
      return (
        <Box sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('lessons:editor.contentPreview')}
          </Typography>
          {lesson.contentBlocks && lesson.contentBlocks.map((block, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('lessons:editor.block')} {index + 1} ({block.type})
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {renderBlockPreview(block.content, block.type)}
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    // Для одиночного контента
    return (
      <Box sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('lessons:editor.contentPreview')}
        </Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {lesson.content ? (
            renderBlockPreview(lesson.content, lesson.contentType || 'standard')
          ) : (
            <Typography color="text.secondary">
              {t('lessons:editor.noContent')}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  // Исправленная логика табов: Tabs должен вызывать setTab с новым значением
  // Если используется кастомный Tabs, он должен поддерживать onChange={(value) => setTab(value)}

  return (
    <Card sx={{ mb: 4, bgcolor: 'background.default', boxShadow: 'none' }}>
      {/* Шапка карточки: заголовок и (опционально) кнопка удаления */}
      {header && <>{header}</>}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Лівий стовпчик: title, duration, type, status */}
        <Grid item xs={12} lg={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            {/* Title */}
            <TextField
              fullWidth
              required
              label={t('lessonEditor.title')}
              name="title"
              value={lesson.title}
              onChange={handleFieldChange}
              placeholder={t('lessonEditor.titlePlaceholder')}
            />
            
            {/* Duration */}
            <TextField
              fullWidth
              label={t('lessonEditor.duration')}
              name="duration"
              type="number"
              value={lesson.duration || 1}
              onChange={handleFieldChange}
              InputProps={{ inputProps: { min: 1 } }}
              sx={{
                '& .MuiInputBase-root': {
                  height: '48px',
                  minHeight: '48px'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  height: '48px'
                },
                '& .MuiInputBase-input': {
                  height: '48px',
                  padding: '12px 14px'
                }
              }}
            />
            
            {/* Type */}
            <Select
              name="type"
              value={lesson.type}
              onChange={handleFieldChange}
              label={t('lessonEditor.type')}
              sx={{
                '& .MuiInputBase-root': {
                  height: '48px',
                  minHeight: '48px'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  height: '48px'
                },
                '& .MuiSelect-select': {
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              <MenuItem value="text">{t('lessonEditor.types.text')}</MenuItem>
              <MenuItem value="video">{t('lessonEditor.types.video')}</MenuItem>
              <MenuItem value="interactive">
                {t('lessonEditor.types.interactive')}
              </MenuItem>
            </Select>

            {/* Professions */}
            <Box sx={{ flex: 1, minHeight: 'auto' }}>
              <CustomAutocomplete
                label={t('lessonEditor.professions')}
                placeholder={t('lessonEditor.professionsPlaceholder')}
                options={professions}
                loading={professionsLoading}
                onChange={handleProfessionsChange}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.term_group?.MainTerm?.value ||
                         option.term_group?.name ||
                         option.name ||
                         option.title ||
                         String(option.id || option._id || '');
                }}
                selectedChips={professions.filter(prof => {
                  const profId = String(prof.id || prof._id);
                  return (lesson.professions || []).some(lessonProf => {
                    const lessonProfId = typeof lessonProf === 'object' 
                      ? String(lessonProf.id || lessonProf._id) 
                      : String(lessonProf);
                    return profId === lessonProfId;
                  });
                })}
                onChipDelete={handleProfessionChipDelete}
                chipColor="primary"
                zIndex={2}
              />
            </Box>

            {/* Skills - включает полную информацию о responsibilities и tools */}
            <Box sx={{ flex: 1, minHeight: 'auto' }}>
              <CustomAutocomplete
                label={t('lessonEditor.skills')}
                placeholder={t('lessonEditor.skillsPlaceholder')}
                options={skills}
                loading={skillsLoading}
                onChange={handleSkillsChange}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.name ||
                         option.term_group?.MainTerm?.value ||
                         option.term_group?.name ||
                         option.title ||
                         String(option.id || option._id || '');
                }}
                selectedChips={skills.filter(skill => {
                  const skillId = String(skill.id || skill._id);
                  return (lesson.skills || []).some(lessonSkill => {
                    const lessonSkillId = typeof lessonSkill === 'object'
                      ? String(lessonSkill.id || lessonSkill._id)
                      : String(lessonSkill);
                    return skillId === lessonSkillId;
                  });
                })}
                onChipDelete={handleSkillChipDelete}
                chipColor="primary"
                zIndex={3}
              />
            </Box>
            {/* Lesson description (new field) */}
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('lessonEditor.description')}
              name="description"
              value={lesson.description || ''}
              onChange={handleFieldChange}
              placeholder={t('lessonEditor.descriptionPlaceholder')}
              sx={{ mt: 1 }}
            />
          </Box>
        </Grid>
        
        {/* Правий стовпчик: image url, image upload, photo preview */}
        <Grid item xs={12} lg={5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Lesson Image Section */}
            <Box>
              <Typography variant="body2" fontWeight={500} mb={2}>
                {t('lessonEditor.lessonImage')}
              </Typography>

              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 3,
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  minHeight: '150px',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover'
                  }
                }}
                onClick={() => document.getElementById(`lesson-image-upload-${uniqueId || lesson._id || 'new'}`).click()}
              >
                {(lesson.imagePreview || lesson.image) ? (
                  <img
                    src={lesson.imagePreview || lesson.image}
                    alt="Lesson"
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '12px'
                    }}
                    onLoad={() => {
                      console.log('Image displayed successfully');
                      console.log('Image source:', lesson.imagePreview || lesson.image);
                    }}
                    onError={(e) => {
                      console.error('Image failed to load:', e);
                      console.error('Image URL:', lesson.imagePreview || lesson.image);
                      console.error('Lesson data:', lesson);
                    }}
                  />
                ) : (
                  <>
                    <AddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t('lessonEditor.clickToUpload')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('lessonEditor.imageRecommendedSize')}
                    </Typography>
                  </>
                )}
              </Box>

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id={`lesson-image-upload-${uniqueId || lesson._id || 'new'}`}
                type="file"
                onChange={handleImageUpload}
                onClick={() => {
                  console.log('Input clicked');
                  console.log('Input ID:', `lesson-image-upload-${uniqueId || lesson._id || 'new'}`);
                }}
              />

              {(lesson.imagePreview || lesson.image) && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                  sx={{ mt: 2 }}
                >
                  {t('lessonEditor.removeImage')}
                </Button>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                  OR
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>

              {/* Image Field */}
              <TextField
                fullWidth
                size="small"
                placeholder={t('lessonEditor.pasteImageUrl')}
                name="image"
                value={lesson.image || ''}
                onChange={handleImageUrlChange}
              />
            </Box>

            {/* Status Section */}
            {isAdminOrTeacher && (
              <Box>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  {t('lessonEditor.status')}
                </Typography>
                <Select
                  name="isDraft"
                  value={lesson.isDraft === undefined ? false : lesson.isDraft}
                  onChange={handleFieldChange}
                  fullWidth
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '48px',
                      minHeight: '48px'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      height: '48px'
                    },
                    '& .MuiSelect-select': {
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                >
                  <MenuItem value={false}>{t('lessonEditor.published')}</MenuItem>
                  <MenuItem value={true}>{t('lessonEditor.draft')}</MenuItem>
                </Select>
              </Box>
            )}
          </Box>
        </Grid>
        {lesson.type === 'video' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('lessonEditor.videoUrl')}
              name="videoUrl"
              value={lesson.videoUrl || ''}
              onChange={handleFieldChange}
              helperText={t('lessonEditor.videoUrlHelp')}
            />
          </Grid>
        )}
      </Grid>

      {/* === Tabs Navigation === */}
      <Box sx={{ mb: 4, mt: 3 }}>
        <Box
          sx={({ palette }) => ({
            display: 'flex',
            width: '100%',
            maxWidth: { md: '70%' },
            p: 0.5,
            bgcolor: palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
            borderRadius: '50px',
            gap: 0.5,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
          })}
        >
          <Button
            variant="text"
            onClick={() => setActiveTab('content')}
            startIcon={<ViewModuleIcon sx={{ fontSize: '16px' }} />}
            sx={({ palette }) => ({
              flex: 1,
              borderRadius: '50px',
              py: 1,
              px: 2,
              color: activeTab === 'content' ? '#ffffff' : 'text.secondary',
              fontWeight: activeTab === 'content' ? 600 : 400,
              textTransform: 'none',
              fontSize: '0.875rem',
              minWidth: 'auto',
              bgcolor: activeTab === 'content' ? '#4299e1' : 'transparent',
              boxShadow: activeTab === 'content' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: activeTab === 'content'
                  ? '#4299e1'
                  : palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
              }
            })}
          >
            {t('lessonEditor.tabs.content')}
          </Button>
          <Button
            variant="text"
            onClick={() => setActiveTab('materials')}
            startIcon={<AttachFileIcon sx={{ fontSize: '16px' }} />}
            sx={({ palette }) => ({
              flex: 1,
              borderRadius: '50px',
              py: 1,
              px: 2,
              color: activeTab === 'materials' ? '#ffffff' : 'text.secondary',
              fontWeight: activeTab === 'materials' ? 600 : 400,
              textTransform: 'none',
              fontSize: '0.875rem',
              minWidth: 'auto',
              bgcolor: activeTab === 'materials' ? '#4299e1' : 'transparent',
              boxShadow: activeTab === 'materials' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: activeTab === 'materials'
                  ? '#4299e1'
                  : palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
              }
            })}
          >
            {t('lessonEditor.tabs.materials')}
          </Button>
          <Button
            variant="text"
            onClick={() => setActiveTab('practice')}
            startIcon={<CodeIcon sx={{ fontSize: '16px' }} />}
            sx={({ palette }) => ({
              flex: 1,
              borderRadius: '50px',
              py: 1,
              px: 2,
              color: activeTab === 'practice' ? '#ffffff' : 'text.secondary',
              fontWeight: activeTab === 'practice' ? 600 : 400,
              textTransform: 'none',
              fontSize: '0.875rem',
              minWidth: 'auto',
              bgcolor: activeTab === 'practice' ? '#4299e1' : 'transparent',
              boxShadow: activeTab === 'practice' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: activeTab === 'practice'
                  ? '#4299e1'
                  : palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
              }
            })}
          >
            {t('lessonEditor.tabs.practice')}
          </Button>
          {showTestsTab && (
            <Button
              variant="text"
              onClick={() => setActiveTab('test')}
              startIcon={<AssignmentIcon sx={{ fontSize: '16px' }} />}
              sx={({ palette }) => ({
                flex: 1,
                borderRadius: '50px',
                py: 1,
                px: 2,
                color: activeTab === 'test' ? '#ffffff' : 'text.secondary',
                fontWeight: activeTab === 'test' ? 600 : 400,
                textTransform: 'none',
                fontSize: '0.875rem',
                minWidth: 'auto',
                bgcolor: activeTab === 'test' ? '#4299e1' : 'transparent',
                boxShadow: activeTab === 'test' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: activeTab === 'test'
                    ? '#4299e1'
                    : palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                }
              })}
            >
              {t('lessonEditor.tabs.test')}
            </Button>
          )}
        </Box>
      </Box>

      {/* === Content Tab === */}
      {activeTab === 'content' && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'transparent' }}>
            {/* Если режим mixed или есть блоки контента — показываем редактор блоков */}
            {lesson.contentType === 'mixed' ||
            (lesson.contentBlocks && lesson.contentBlocks.length > 0) ? (
              <>
                <ContentBlocksEditor
                  blocks={lesson.contentBlocks || []}
                  onChange={handleContentBlocksChange}
                  onAddBlock={addContentBlock}
                  onRemoveBlock={removeContentBlock}
                  onSwitchToSingle={switchToSingleContent}
                  isMultiBlockMode={true}
                />
                {/* Не показываем кнопку превью для типов контента с встроенным превью */}
                {!['memoryGame', 'flippingCards', 'labyrinth', 'tagCloud', 'rolePlayGame'].includes(lesson.contentType) && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setContentPreviewOpen(!contentPreviewOpen)}
                        startIcon={contentPreviewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      >
                        {contentPreviewOpen ? 'Hide Preview' : 'Preview Content'}
                      </Button>
                    </Box>
                    {renderContentPreview()}
                  </>
                )}
              </>
            ) : (
              <>
                <ContentTypeEditor
                  contentType={lesson.contentType || 'standard'}
                  content={lesson.content}
                  onContentTypeChange={handleContentTypeChange}
                  onContentChange={handleContentChange}
                  onSwitchToBlocks={switchToContentBlocks}
                  onSwitchToSingle={switchToSingleContent}
                  isMultiBlockMode={false}
                />
                {/* Не показываем кнопку превью для типов контента с встроенным превью */}
                {!['memoryGame', 'flippingCards', 'labyrinth', 'tagCloud', 'rolePlayGame'].includes(lesson.contentType || 'standard') && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setContentPreviewOpen(!contentPreviewOpen)}
                        startIcon={contentPreviewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      >
                        {contentPreviewOpen ? 'Hide Preview' : 'Preview Content'}
                      </Button>
                    </Box>
                    {renderContentPreview()}
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* === Materials Tab === */}
      {activeTab === 'materials' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('lessonEditor.resourcesTitle')}
          </Typography>

          {/* List of existing materials */}
          {lesson.resources && lesson.resources.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {lesson.resources.map((resource, idx) => (
                <Card key={idx} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {resource.title || t('lessonEditor.noMaterials')}
                      </Typography>
                      {resource.type && (
                        <Typography variant="caption" color="text.secondary">
                          {resource.type}
                        </Typography>
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeResource(idx)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('lessonEditor.resourceTitle')}
                      name="title"
                      value={resource.title}
                      onChange={(e) => handleResourceChange(idx, e)}
                      placeholder="e.g., Presentation.pdf"
                    />
                    <TextField
                      fullWidth
                      label={t('lessonEditor.resourceDescription')}
                      name="description"
                      value={resource.description}
                      onChange={(e) => handleResourceChange(idx, e)}
                      multiline
                      rows={2}
                      placeholder={t('lessonEditor.descriptionPlaceholder')}
                    />
                    <Select
                      name="type"
                      value={resource.type || 'Link'}
                      onChange={(e) => handleResourceChange(idx, e)}
                      label={t('lessonEditor.resourceType')}
                    >
                      <MenuItem value="PDF">PDF</MenuItem>
                      <MenuItem value="Cheatsheet">
                        {t('lessonEditor.resourceTypes.cheatsheet')}
                      </MenuItem>
                      <MenuItem value="Guide">
                        {t('lessonEditor.resourceTypes.guide')}
                      </MenuItem>
                      <MenuItem value="Tutorial">
                        {t('lessonEditor.resourceTypes.tutorial')}
                      </MenuItem>
                      <MenuItem value="Article">
                        {t('lessonEditor.resourceTypes.article')}
                      </MenuItem>
                      <MenuItem value="Link">
                        {t('lessonEditor.resourceTypes.link')}
                      </MenuItem>
                      <MenuItem value="Jupyter Notebook">
                        Jupyter Notebook
                      </MenuItem>
                    </Select>
                    <TextField
                      fullWidth
                      required
                      label={t('lessonEditor.resourceUrl')}
                      name="url"
                      value={resource.url}
                      onChange={(e) => handleResourceChange(idx, e)}
                      placeholder="Paste URL..."
                    />
                  </Box>
                </Card>
              ))}
            </Box>
          )}

          {/* Add Material Button */}
          {(!lesson.resources || lesson.resources.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t('lessonEditor.noMaterials')}
              </Typography>
            </Box>
          )}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addResource}
            fullWidth
          >
            {t('lessonEditor.addMaterial')}
          </Button>
        </Box>
      )}

      {/* === Practice Tab === */}
      {activeTab === 'practice' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('lessonEditor.practiceTitle')}
          </Typography>

          {/* List of existing exercises */}
          {lesson.practiceExercises && lesson.practiceExercises.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {lesson.practiceExercises.map((exercise, idx) => (
                <Card key={idx} sx={{ mb: 2, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6">
                      {t('lessonEditor.exerciseItem')} {idx + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeExercise(idx)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('lessonEditor.exerciseTitle')}
                      name="title"
                      value={exercise.title}
                      onChange={(e) => handleExerciseChange(idx, e)}
                    />
                    <TextField
                      fullWidth
                      label={t('lessonEditor.exerciseDescription')}
                      name="description"
                      value={exercise.description}
                      onChange={(e) => handleExerciseChange(idx, e)}
                      multiline
                      rows={3}
                    />
                    <TextField
                      fullWidth
                      label={t('lessonEditor.exerciseCodeSnippet')}
                      name="codeSnippet"
                      value={exercise.codeSnippet}
                      onChange={(e) => handleExerciseChange(idx, e)}
                      multiline
                      rows={5}
                      helperText={t('lessonEditor.exerciseCodeHelp')}
                    />
                    <TextField
                      fullWidth
                      label={t('lessonEditor.exercisePlaygroundUrl')}
                      name="playgroundUrl"
                      value={exercise.playgroundUrl}
                      onChange={(e) => handleExerciseChange(idx, e)}
                      helperText={t('lessonEditor.exercisePlaygroundHelp')}
                    />
                  </Box>
                </Card>
              ))}
            </Box>
          )}

          {/* Add Exercise Button */}
          {(!lesson.practiceExercises || lesson.practiceExercises.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t('lessonEditor.noExercises')}
              </Typography>
            </Box>
          )}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addExercise}
            fullWidth
          >
            {t('lessonEditor.addExercise')}
          </Button>
        </Box>
      )}

      {/* === Test Tab === */}
      {activeTab === 'test' && showTestsTab && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuizIcon fontSize="small" />
              {t('lessonEditor.lessonTests')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TestsArrayJsonImport
                onSuccess={(modIdx, lesIdx, testsArray) => {
                  handleTestsArrayImport(testsArray);
                }}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addTest}
                size="small"
              >
                {t('lessonEditor.addTest')}
              </Button>
            </Box>
          </Box>

          {/* Tests List */}
          {lesson.tests && lesson.tests.length > 0 ? (
            lesson.tests.map((test, testIndex) => (
              <TestEditor
                key={test._id || `test-${testIndex}`}
                test={test}
                testIndex={testIndex}
                onChange={(updatedTest) => handleTestChange(updatedTest, testIndex)}
                onRemove={() => handleOpenDeleteDialog(test._id)}
              />
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t('lessonEditor.noTests')}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Модальное окно подтверждения удаления теста */}
      <Dialog
        open={deleteTestDialog.open}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-test-dialog-title"
        aria-describedby="delete-test-dialog-description"
      >
        <DialogTitle id="delete-test-dialog-title">
          {t('lessonEditor.deleteTestDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-test-dialog-description">
            {t('lessonEditor.deleteTestDialogMessage', { title: deleteTestDialog.testTitle })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            {t('lessonEditor.deleteTestDialogCancel')}
          </Button>
          <Button onClick={handleConfirmDeleteTest} color="error" variant="contained" autoFocus>
            {t('lessonEditor.deleteTestDialogConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

LessonEditor.displayName = 'LessonEditor';

export default LessonEditor;
